// The bank's items (RFC-056 §3): the list — every item the person sees,
// narrowed to theirs, to those waiting on an admin's review, or by status —
// and an item's page: its versions, each with its check and what its
// review said, what it measures, and what the person may do to it. Laid
// out as the Exams pages are (byj-1wv.51.18): rows with their kind and
// their state, an item's page under a bar of how it stands and what may be
// done, its rarer and destructive actions behind its ⋯.
import { useCallback, useEffect, useState } from "react";
import type { BankClient, BankItem, BankMe, ItemMeasure, ItemVersion } from "../bank";
import { t, whenMinute, word } from "../i18n";
import { download } from "../download";
import { navigate } from "../router";
import { useConfirm } from "./Confirm";
import { FilterChips, inForce, useFilter, type Chip } from "./FilterChips";
import { FormDialog } from "./FormDialog";
import { Link } from "./Nav";
import { RowMenu, type MenuItem } from "./RowMenu";
import { EmptyRows, Grouped, initialOf } from "./Rows";
import { StateBar } from "./StateBar";
import { SubmitDialog, submitDialogId } from "./BankSubmit";
import { Choices } from "./Choices";
import { AuthorCommands, CommandLine } from "./CommandLine";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

export const statusWord = (s: string) => word("bank:status", s) ?? s;
export const stateWord = (s: string) => word("bank:state", s) ?? s;
/** A version's state that asks something of its author: a check failed, a review sent it back. */
const sentBack = (s?: string) => s === "invalid" || s === "rejected" || s === "changes_requested";

/** What an item measures, in a line: its family, its competencies by share, its difficulty and minutes. */
export function measureLine(m?: ItemMeasure): string {
  if (!m) return "";
  const comps = Object.entries(m.competencies ?? {})
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${word("bank:competency", k) ?? k} ${Math.round(v * 100)}%`)
    .join(", ");
  const diff = m.difficulty?.measured !== undefined ? t("bank:measured", { p: Math.round(m.difficulty.measured * 100) }) : m.difficulty?.authored !== undefined ? t("bank:authored", { n: m.difficulty.authored }) : "";
  return [m.family && (word("bank:family", m.family) ?? m.family), comps, diff, m.minutes && t("bank:minutes", { n: m.minutes })].filter(Boolean).join(" · ");
}

/** How an item did where it was sat, in a line: the bank's measure. */
export function satLine(it: BankItem): string {
  const m = it.measure;
  if (!m) return "";
  return [
    t("bank:sat", { n: m.n, count: m.assignments }),
    m.right !== undefined && t("bank:right", { p: Math.round(m.right * 100) }),
    m.discrimination !== undefined && t("bank:discrimination", { d: m.discrimination.toFixed(2) }),
  ]
    .filter(Boolean)
    .join(" · ");
}

/** An item's row: what it measures and who wrote it, its status, and its latest version's state when that is not settled. */
function ItemRow({ it }: { it: BankItem }) {
  const sub = [measureLine(it.meta?.item), satLine(it), it.author_email].filter(Boolean);
  const pending = !!it.latest_state && it.latest_state !== "accepted";
  return (
    <li className={`share-row no-menu ${it.status === "retired" ? "is-ended" : "is-live"}`} data-testid="bank-item">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        itm
      </span>
      <div className="row-main">
        <Link to={`/bank/items/${encodeURIComponent(it.id)}`} className="row-title">
          {it.title || it.id}
        </Link>
        <div className="row-sub">{sub.join(" · ")}</div>
      </div>
      <div className="row-meta">
        {/* one word for where it stands: a version not yet accepted says it while the item was never accepted; once it was, its status leads and a later version's state goes beside it */}
        {pending && it.status === "submitted" ? (
          <span className={`chip${sentBack(it.latest_state) ? " chip-warn" : ""}`}>{stateWord(it.latest_state!)}</span>
        ) : (
          <>
            {pending && <span className={`chip${sentBack(it.latest_state) ? " chip-warn" : ""}`}>{t("bank:latestState", { v: it.latest, state: stateWord(it.latest_state!) })}</span>}
            <span className={`chip${it.status === "active" ? " chip-live" : ""}`}>{statusWord(it.status)}</span>
          </>
        )}
      </div>
    </li>
  );
}

/** The items: a filter over them, the admins' review queue first; `submit` given, its dialog is open (/bank/submit). */
export function BankItems({ client, me, submit }: { client: BankClient; me: BankMe; submit?: { item?: string } }) {
  const admin = me.role === "admin";
  const [items, setItems] = useState<BankItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useFilter("bank-items");
  const [submitting, setSubmitting] = useState<{ item?: string } | null>(submit ?? null);
  // the dialog follows the address: Back to /bank/submit opens it, Back from it closes it
  const asked = submit ? submit.item ?? "" : null;
  useEffect(() => {
    setSubmitting(asked === null ? null : { item: asked || undefined });
  }, [asked]);
  useEffect(() => {
    client.items().then(
      (r) => setItems(r.items),
      (err: unknown) => setError(errText(err)),
    );
  }, [client]);
  const all = items ?? [];
  const groups: Record<string, (x: BankItem) => boolean> = {
    all: () => true,
    mine: (x) => x.mine,
    review: (x) => x.latest_state === "in_review",
    active: (x) => x.status === "active",
  };
  const chips: Chip[] = [
    { value: "all", label: t("bank:filterAll"), count: all.length },
    { value: "mine", label: t("bank:filterMine"), count: all.filter(groups.mine!).length },
    ...(admin ? [{ value: "review", label: t("bank:filterReview"), count: all.filter(groups.review!).length }] : []),
    { value: "active", label: t("bank:filterActive"), count: all.filter(groups.active!).length },
  ];
  const f = inForce(filter, chips);
  const shown = all.filter(groups[f] ?? groups.all!);
  // an admin sees first what waits on them, under its own heading
  const waiting = admin && f === "all" ? shown.filter(groups.review!) : [];
  const close = () => {
    setSubmitting(null);
    if (submit) navigate("/bank/items");
  };
  return (
    <div data-testid="bank-items">
      <div className="panel-head">
        <p className="muted small panel-lead">
          {t("bank:itemsLead")} {me.settings.catalog === "public" ? t("bank:catalogPublic") : t("bank:catalogMetadata")}
        </p>
        <button className="primary" aria-haspopup="dialog" aria-expanded={!!submitting} aria-controls={submitting ? submitDialogId : undefined} onClick={() => setSubmitting({})}>
          {t("bank:navSubmit")}
        </button>
      </div>
      <StatusLegend />
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {items === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : (
        <Grouped<BankItem>
          id="bank-items"
          headed={waiting.length > 0}
          tools={all.length > 0 && <FilterChips label={t("bank:filterLabel")} chips={chips} value={f} onChange={setFilter} />}
          groups={[
            { title: t("bank:itemsWaiting"), key: "waiting", className: "rows-head-attention", items: waiting },
            {
              title: t("bank:itemsHead"),
              key: "items",
              items: shown.filter((x) => !waiting.includes(x)),
              // nothing to say when what is shown all waits on review, under its own heading above
              none:
                shown.length > 0 ? undefined : all.length === 0 ? (
                  // nothing in the bank at all: how an item gets in
                  <div className="empty-card stack" data-testid="bank-items-none">
                    <p className="muted small flush">{t("bank:itemsNoneHow")}</p>
                    <CommandLine command="byj exam bank submit KIT QUESTION --note '…'" />
                  </div>
                ) : (
                  <EmptyRows text={t("bank:itemsNone")} />
                ),
            },
          ]}
          row={(it) => <ItemRow key={it.id} it={it} />}
        />
      )}
      {submitting && (
        <SubmitDialog
          client={client}
          item={submitting.item}
          onDone={(id) => {
            setSubmitting(null);
            navigate(`/bank/items/${encodeURIComponent(id)}`);
          }}
          onClose={close}
        />
      )}
    </div>
  );
}

/** What each status and state on the list means, folded under the lead: the words a row's chips say. */
const legend = ["submitted", "accepted", "calibrated", "active", "retired", "invalid", "changes_requested", "rejected"] as const;
/** The legend's words that are a version's state, not the item's status. */
const legendStates = new Set<string>(["invalid", "changes_requested", "rejected"]);
function StatusLegend() {
  return (
    <details className="bank-legend" data-testid="bank-legend">
      <summary className="small">{t("bank:legendTitle")}</summary>
      <dl className="small">
        {legend.map((k) => (
          <div key={k}>
            <dt>{legendStates.has(k) ? stateWord(k) : statusWord(k)}</dt>
            <dd className="muted">{t(`bank:legend.${k}`)}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

/** An admin's review of a version: accept, or ask for changes or reject, saying why; each verdict says what follows it. */
function ReviewDialog({ v, onSubmit, onClose }: { v: ItemVersion; onSubmit: (verdict: Verdict, comment: string) => Promise<void>; onClose: () => void }) {
  const [verdict, setVerdict] = useState<Verdict>("accept");
  const [comment, setComment] = useState("");
  const why = verdict !== "accept";
  return (
    <FormDialog
      id="bank-review-dialog"
      testid="bank-review"
      title={t("bank:reviewTitle", { v: v.version })}
      submitLabel={t("bank:reviewSubmit")}
      canSubmit={!why || comment.trim() !== ""}
      onSubmit={() => onSubmit(verdict, comment.trim())}
      onClose={onClose}
    >
      {v.note && <p className="muted small flush">{t("bank:reviewAuthorNote", { note: v.note })}</p>}
      {/* what to look at before deciding: the page under the dialog shows each */}
      <details className="bank-legend" data-testid="bank-review-check">
        <summary className="small">{t("bank:reviewCheckTitle")}</summary>
        <ul className="small bank-check">
          {(["question", "check", "measure", "hidden"] as const).map((k) => (
            <li key={k}>{t(`bank:reviewCheck.${k}`)}</li>
          ))}
        </ul>
      </details>
      <Choices<Verdict>
        name="bank-verdict"
        className="bank-choice"
        legend={t("bank:reviewVerdict")}
        value={verdict}
        onChange={setVerdict}
        options={(["accept", "changes", "reject"] as const).map((x) => ({ value: x, name: t(`bank:verdict.${x}`), what: t(`bank:verdictWhat.${x}`) }))}
      />
      <label className="field">
        {why ? t("bank:reviewCommentNeeded") : t("bank:reviewComment")}
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} aria-label={t("bank:reviewComment")} />
      </label>
    </FormDialog>
  );
}

type Verdict = "accept" | "reject" | "changes";

/** The steps an item goes through, and which it stands at: what the next step asks, and of whom. */
const itemSteps = ["submit", "check", "review", "draw"] as const;
type ItemStep = (typeof itemSteps)[number];

/**
 * Where an item stands and what is next, as its author or an admin reads it;
 * null once it is drawn, or retired. `resubmit`: the author's next step is a
 * new version, and the page says how.
 */
export function itemNext(it: BankItem, admin: boolean): { step: ItemStep; text: string; resubmit?: boolean } | null {
  const versions = it.versions ?? [];
  const last = versions[versions.length - 1];
  if (!last || it.status === "retired") return null;
  const mine = it.mine;
  const v = last.version;
  switch (last.state) {
    case "uploading":
      return { step: "submit", text: t("bank:next.uploading"), resubmit: mine };
    case "checking":
      return { step: "check", text: t("bank:next.checking") };
    case "invalid":
      return { step: "check", text: mine ? t("bank:next.invalidMine", { v }) : t("bank:next.invalidOther", { v }), resubmit: mine };
    case "in_review":
      return { step: "review", text: admin ? t("bank:next.reviewAdmin", { v }) : t("bank:next.reviewWait", { v }) };
    case "changes_requested":
      return { step: "review", text: mine ? t("bank:next.changesMine", { v }) : t("bank:next.changesOther", { v }), resubmit: mine };
    case "rejected":
      return { step: "review", text: mine ? t("bank:next.rejectedMine", { v }) : t("bank:next.rejectedOther", { v }), resubmit: mine };
    case "withdrawn":
      return mine ? { step: "submit", text: t("bank:next.withdrawnMine", { v }), resubmit: true } : null;
    default:
      if (it.status === "accepted" || it.status === "calibrated") return { step: "draw", text: admin ? t("bank:next.drawAdmin") : t("bank:next.drawWait") };
      return null;
  }
}

/** What is next on an item's page: its steps, the one it stands at, and what that step asks (the Exams page's NextStep). */
function ItemNext({ step, text, resubmit }: { step: ItemStep; text: string; resubmit?: boolean }) {
  const at = itemSteps.indexOf(step);
  return (
    <section className="card next-step" aria-labelledby="bank-next-title" data-testid="bank-item-next" data-step={step}>
      <ol className="stage-steps" aria-label={t("bank:nextStep")}>
        {itemSteps.map((s, i) => (
          <li key={s} className={i < at ? "is-done" : i === at ? "is-now" : ""} aria-current={i === at ? "step" : undefined}>
            <span className="stage-mark" aria-hidden="true">
              {i < at ? "✓" : i + 1}
            </span>
            {t(`bank:step.${s}`)}
          </li>
        ))}
      </ol>
      <div className="stack next-step-body">
        <h2 id="bank-next-title" className="flush">
          {t("bank:nextStep")}: <span className="next-step-what">{text}</span>
        </h2>
        {resubmit && (
          // the two ways a new version goes in: the bar's button, or byj on the author's machine
          <>
            <p className="muted small flush">{t("bank:next.resubmitHow")}</p>
            <AuthorCommands commands={["byj exam validate KIT", "byj exam bank submit KIT QUESTION --note '…'"]} />
          </>
        )}
      </div>
    </section>
  );
}

/** What the bank's ids are called — an item's, a blueprint's, a paper's, a request's — by id: what a job's or an audit row's target says. */
export type BankNames = Record<string, string>;

/** The names of the items the person sees, and with `all` the blueprints and an admin's papers and requests: each read once, an id standing for what cannot be read. */
export function useItemTitles(client: BankClient, all = false): BankNames {
  const [names, setNames] = useState<BankNames>({});
  useEffect(() => {
    const add = (entries: [string, string][]) => setNames((n) => ({ ...n, ...Object.fromEntries(entries) }));
    // names only: a failure leaves the ids
    const quietly = <T,>(read: () => Promise<T>, then: (x: T) => void) => void Promise.resolve().then(read).then(then, () => {});
    quietly(() => client.items(), (r) => add(r.items.map((x) => [x.id, x.title || x.id])));
    if (!all) return;
    quietly(() => client.papers(true), (ps) => add(ps.map((p) => [p.id, p.title])));
    quietly(() => client.blueprints(), (bs) => add(bs.map((b) => [b.id, b.title || b.id])));
    quietly(() => client.requests(true), (rs) => add(rs.map((r) => [r.id, t("bank:requestFor", { klass: r.class_name })])));
  }, [client, all]);
  return names;
}

/** A target as the bank names it — itm_…@2, bpr_…@1, a hand-in or an assignment of `paper`, req_… — by the names of what it is; null when they are not known. */
export function targetName(target: string, names: BankNames, paper?: string): string | null {
  // a version of an item or of a blueprint (a validate's, a trial's): itm_…@2, bpr_…@1
  const at = /^([^@/]+)@(\d+)$/.exec(target);
  if (at) {
    const title = names[at[1]!];
    return title ? `${title} · ${t("bank:versionN", { n: Number(at[2]) })}` : null;
  }
  // a grade's, a regrade's: the target is the hand-in, an analysis's the assignment the paper was sat in; either is
  // said by its paper (a job's input, an audit row's detail), the target's own id shown beside the name where it is used
  if (paper && names[paper]) return names[paper];
  return names[target] ?? null;
}

/** The paper a job or an audit row names beside its target, when that is a paper's hand-in or the assignment it was sat in. */
export const paperOf = (x: { input?: { paper?: string } | null; detail?: Record<string, unknown> }): string | undefined =>
  x.input?.paper ?? (typeof x.detail?.paper === "string" ? x.detail.paper : undefined);

/** A version: its note and when, why it was sent back, what its check found. */
function VersionRow({ v, current }: { v: ItemVersion; current: boolean }) {
  const problems = v.validation?.problems ?? [];
  return (
    <li className={`share-row no-menu ${v.state === "withdrawn" || v.state === "rejected" ? "is-ended" : "is-live"}`} data-testid="bank-version">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        v{v.version}
      </span>
      <div className="row-main">
        <span className="row-title">{t("bank:versionN", { n: v.version })}</span>
        <div className="row-sub">{[v.note, v.submitted_at && whenMinute(v.submitted_at)].filter(Boolean).join(" · ")}</div>
        {v.why && <p className={`small flush ${sentBack(v.state) ? "error" : ""}`}>{v.why}</p>}
        {problems.length > 0 && v.state === "invalid" && (
          <ul className="small flush bank-problems">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
      </div>
      <div className="row-meta">
        {current && <span className="chip chip-on">{t("bank:versionDrawn")}</span>}
        <span className={`chip${sentBack(v.state) ? " chip-warn" : ""}`}>{stateWord(v.state)}</span>
      </div>
    </li>
  );
}

type Review = NonNullable<BankItem["reviews"]>[number];

/** A review: whose verdict on which version, and what they said. */
function ReviewRow({ r }: { r: Review }) {
  return (
    <li className="share-row no-menu is-live" data-testid="bank-review-row">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {initialOf(r.email)}
      </span>
      <div className="row-main">
        <span className="row-title">
          {t("bank:versionN", { n: r.version })} · {t(`bank:verdict.${r.verdict}`)}
        </span>
        <div className="row-sub">{[r.email, whenMinute(r.at)].join(" · ")}</div>
        {r.comment && <p className="small flush">{r.comment}</p>}
      </div>
    </li>
  );
}

/** The question an item asks, as its candidate reads it: a choice question's options under it. */
function Prompt({ q }: { q: NonNullable<NonNullable<BankItem["meta"]>["questions"]>[number] }) {
  return (
    <section className="bank-prompt" data-testid="bank-item-prompt">
      <h3 className="rows-head">{t("bank:prompt")}</h3>
      <p className="flush">{q.prompt}</p>
      {(q.options ?? []).length > 0 && (
        <ol className="bank-options small">
          {q.options!.map((o) => (
            <li key={o}>{o}</li>
          ))}
        </ol>
      )}
    </section>
  );
}

/** How an item stands, as its bar's dot: set in papers, retired, or neither yet. */
const barState = (s: string) => (s === "active" ? "live" : s === "retired" ? "ended" : "paused");

export function BankItemPage({ client, id, admin }: { client: BankClient; id: string; admin: boolean }) {
  const [it, setIt] = useState<BankItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ask, confirmDialog] = useConfirm();
  const load = useCallback(async () => {
    try {
      setIt(await client.item(id));
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
  }, [client, id]);
  useEffect(() => {
    void load();
  }, [load]);
  // a version being checked is looked at again until it is; one waiting for
  // its kit is not, since a kit that never arrived never will on its own
  useEffect(() => {
    if (it?.latest_state !== "checking") return;
    const timer = setTimeout(() => void load(), 3000);
    return () => clearTimeout(timer);
  }, [it, load]);
  if (!it) return error ? <p className="error">{t("common:error", { m: error })}</p> : <p className="muted">{t("common:loading")}</p>;
  const can = it.can ?? {};
  const versions = it.versions ?? [];
  const last = versions[versions.length - 1];
  const act = async (what: () => Promise<unknown>, said: string) => {
    try {
      await what();
      setDone(said);
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
    await load();
  };
  const q = it.meta?.questions?.[0];
  const get = async (part: "public" | "hidden") => {
    if (part === "hidden" && !(await ask(t("bank:hiddenAsk"), { action: t("bank:hiddenRead") }))) return;
    try {
      const b = await client.kit(it.id, it.current || it.latest, part);
      const url = URL.createObjectURL(b);
      download(url);
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      setError(errText(err));
    }
  };
  // the rarer and the destructive, behind the ⋯, the destructive last
  const menu: MenuItem[] = [
    ...(can.public ? [{ label: t("bank:getPublic"), onSelect: () => void get("public") }] : []),
    ...(can.hidden ? [{ label: t("bank:getHidden"), onSelect: () => void get("hidden") }] : []),
    ...(can.withdraw
      ? [{ label: t("bank:withdraw"), onSelect: async () => {
          if (await ask(t("bank:withdrawAsk"), { action: t("bank:withdraw") })) await act(() => client.withdraw(it.id), t("bank:didWithdraw"));
        } }]
      : []),
    ...(can.retire
      ? [{ label: t("bank:retire"), danger: true, onSelect: async () => {
          if (await ask(t("bank:retireAsk"), { action: t("bank:retire"), danger: true })) await act(() => client.setStatus(it.id, "retired"), t("bank:didRetire"));
        } }]
      : []),
    ...(can.delete
      ? [{ label: t("bank:delete"), danger: true, onSelect: async () => {
          if (!(await ask(t("bank:deleteAsk"), { action: t("bank:delete"), danger: true }))) return;
          try {
            await client.deleteItem(it.id);
            navigate("/bank/items");
          } catch (err) {
            setError(errText(err));
          }
        } }]
      : []),
  ];
  const facts = [measureLine(it.meta?.item), it.author_email && t("bank:by", { email: it.author_email })].filter(Boolean).join(" · ");
  const next = itemNext(it, admin);
  return (
    <section className="stack bank-item" data-testid="bank-item-page">
      <p className="small flush">
        <Link to="/bank/items" className="muted">
          ← {t("bank:backToItems")}
        </Link>
      </p>
      <header className="page-head bank-item-head">
        <h2 className="flush">{it.title || it.id}</h2>
        <RowMenu label={t("shares:moreFor", { title: it.title || it.id })} items={menu} />
      </header>
      <StateBar
        className="exam-bar"
        label={t("bank:itemBar")}
        state={barState(it.status)}
        stateLabel={<span className="share-bar-state">{statusWord(it.status)}</span>}
        testid="bank-item-bar"
        actions={
          <>
            {can.review && last && (
              <button className="small primary" aria-haspopup="dialog" aria-controls={reviewing ? "bank-review-dialog" : undefined} onClick={() => setReviewing(true)}>
                {t("bank:review")}
              </button>
            )}
            {can.calibrate && <button className="small" onClick={() => void act(() => client.setStatus(it.id, "calibrated"), t("bank:didCalibrate"))}>{t("bank:calibrate")}</button>}
            {can.activate && <button className="small" onClick={() => void act(() => client.setStatus(it.id, "active"), t("bank:didActivate"))}>{t("bank:activate")}</button>}
            {can.new_version && (
              <button className="small" aria-haspopup="dialog" aria-controls={submitting ? submitDialogId : undefined} onClick={() => setSubmitting(true)}>
                {t("bank:newVersion")}
              </button>
            )}
          </>
        }
      >
        {facts && <span className="share-bar-facts">{facts}</span>}
      </StateBar>
      {next && <ItemNext step={next.step} text={next.text} resubmit={next.resubmit} />}
      {it.measure && (
        <p className="small flush" data-testid="bank-item-measure">
          {satLine(it)}
          {(it.measure.why ?? []).length > 0 && <> <span className="chip chip-warn">{it.measure.why!.join("; ")}</span></>}
        </p>
      )}
      {error && <p className="error flush">{t("common:error", { m: error })}</p>}
      {done && (
        <p className="ok-text flush" role="status">
          {done}
        </p>
      )}
      {q?.prompt && <Prompt q={q} />}
      <Grouped<{ v?: ItemVersion; r?: Review }>
        id="bank-item"
        headed
        groups={[
          { title: t("bank:versions"), key: "versions", items: [...versions].reverse().map((v) => ({ v })), testid: "bank-versions" },
          { title: t("bank:reviews"), key: "reviews", items: (it.reviews ?? []).map((r) => ({ r })), testid: "bank-reviews" },
        ]}
        row={(x) => (x.v ? <VersionRow key={`v${x.v.version}`} v={x.v} current={x.v.version === it.current} /> : <ReviewRow key={x.r!.id} r={x.r!} />)}
      />
      {reviewing && last && (
        <ReviewDialog
          v={last}
          onSubmit={async (verdict, comment) => {
            await client.review(it.id, last.version, verdict, comment);
            setReviewing(false);
            setDone(t("bank:reviewed"));
            await load();
          }}
          onClose={() => setReviewing(false)}
        />
      )}
      {submitting && (
        <SubmitDialog
          client={client}
          item={it.id}
          onDone={() => {
            setSubmitting(false);
            setDone(t("bank:submitted"));
            void load();
          }}
          onClose={() => setSubmitting(false)}
        />
      )}
      {confirmDialog}
    </section>
  );
}
